import { HttpErrorResponse, HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { LoadingService, NotificationService } from '@shared/components';
import { LocalStorageService } from '@shared/services';
import { throwError } from 'rxjs';
import { catchError, filter, map } from 'rxjs/operators';

export interface ApiResponse<T> {
  code: number;
  data?: T;
  message: string;
}

// Variable para evitar múltiples redirecciones simultáneas
let isRedirecting = false;

export const apiResponseInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const notificationService = inject(NotificationService);
  const localStorageService = inject(LocalStorageService);
  const loadingService = inject(LoadingService);

  return next(req).pipe(
    filter((event) => event instanceof HttpResponse),
    map((event) => {
      const body = event.body as ApiResponse<unknown>;

      return event.clone({ body: body.data });
    }),
    catchError((error: HttpErrorResponse) => {
      const body = error.error as ApiResponse<unknown>;

      if (error.status === 401) {
        // Evitar redirección y limpieza si estamos en el login
        if (req.url.includes('/auth/login')) {
          loadingService.hide();
          const errorMessage = body?.message || 'Credenciales incorrectas';
          notificationService.showNotification(errorMessage, 'error');
          return throwError(() => error);
        }

        // Evitar redirección múltiple y no redirigir en check-token
        if (!isRedirecting && !req.url.includes('/auth/check-token')) {
          isRedirecting = true;
          localStorageService.removeToken();

          // Usar setTimeout para permitir que la respuesta se procese primero
          setTimeout(() => {
            router.navigate(['/auth']);
            isRedirecting = false;
          }, 100);
        }
      }

      if (body && body.message) {
        const errorMessage = body.message;
        notificationService.showNotification(errorMessage, 'error');
        loadingService.hide();
        const errorResponse: ApiResponse<null> = { code: body.code || 9999, message: errorMessage };

        return throwError(
          () =>
            new HttpErrorResponse({
              status: error.status || 400,
              error: errorResponse,
            }),
        );
      }

      loadingService.hide();
      return throwError(() => error);
    }),
  );
};
